// Geometry builders for the sea frame (THREE, no React): the one-mesh ocean
// ring and the one-mesh mist curtain. Both are built once per terrain
// signature into fixed buffers (renderer-crash-rca: no per-tile geometry).

import * as THREE from 'three';
import { SEA_COLOR } from '../sceneRenderConfig.js';
import { seaColorAt } from '../shore/waterPalette.js';
import { type BoundaryEdge, type RingTile, axialXZ } from './seaTiles.js';

/** Surface Y of the board's outer (shallow) ocean tiles; matches waterSurfaceY. */
export const RING_NEAR_Y = -0.23;
/** Surface Y of deep ocean (waterSurfaceY for a deep tile). */
export const RING_FAR_Y = -0.53;
/** Distance (world units, from the board edge) over which shallow becomes deep. */
export const RING_FADE = 4.2;

// Fallback colours when the caller has no shore field (older callers / tests).
const NEAR_COLOR = new THREE.Color('#27628a');
const FAR_COLOR = new THREE.Color(SEA_COLOR);

const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Distance from point (px,pz) to segment centre (cx,cz) +- tangent*0.5. */
function distToEdge(px: number, pz: number, e: BoundaryEdge): number {
  const tx = -e.nz;
  const tz = e.nx;
  const t = Math.max(-0.5, Math.min(0.5, (px - e.x) * tx + (pz - e.z) * tz));
  return Math.hypot(px - (e.x + tx * t), pz - (e.z + tz * t));
}

/** Water surface Y at distance `d` outside the board edge. */
export function ringSurfaceY(d: number): number {
  return RING_NEAR_Y + (RING_FAR_Y - RING_NEAR_Y) * smooth(0, RING_FADE, d);
}

/** Hex fan rings per tile (same subdivision as TerrainWater's makeHexPlane). */
const FAN = [1 / 3, 2 / 3, 1] as const;
export const VERTS_PER_RING_TILE = 1 + 6 * FAN.length;
export const TRIS_PER_RING_TILE = 6 + 12 * (FAN.length - 1);

/**
 * ONE geometry for every ring tile: hex fans (19 verts, 30 tris each) merged,
 * vertex Y graded from the board's shallows to deep water by distance to
 * `boundary`. Vertex colour is the board's own sea colour for its distance to
 * land (`landDist`, the shore field), so the ring joins the board's water and the
 * OpenSea disc without a seam; without `landDist` it grades shallow -> open sea
 * by distance to the board edge.
 */
export function buildOceanRingGeometry(
  tiles: ReadonlyArray<RingTile>,
  boundary: ReadonlyArray<BoundaryEdge>,
  landDist?: (x: number, z: number) => number,
): THREE.BufferGeometry {
  const nV = tiles.length * VERTS_PER_RING_TILE;
  const pos = new Float32Array(nV * 3);
  const col = new Float32Array(nV * 3);
  const idx = new Uint32Array(tiles.length * TRIS_PER_RING_TILE * 3);
  const c = new THREE.Color();
  let v = 0;
  let ii = 0;
  for (const t of tiles) {
    const [cx, cz] = axialXZ(t.q, t.r);
    const base = v;
    const put = (x: number, z: number) => {
      let d = Number.POSITIVE_INFINITY;
      for (const e of boundary) d = Math.min(d, distToEdge(x, z, e));
      pos.set([x, ringSurfaceY(d), z], v * 3);
      if (landDist) seaColorAt(landDist(x, z), c);
      else c.copy(NEAR_COLOR).lerp(FAR_COLOR, smooth(0, RING_FADE, d));
      col.set([c.r, c.g, c.b], v * 3);
      v++;
    };
    put(cx, cz);
    for (const rad of FAN) {
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 180) * (30 + 60 * i);
        // makeHexPlane builds in XY then rotateX(-PI/2): y -> -z.
        put(cx + rad * Math.cos(a), cz - rad * Math.sin(a));
      }
    }
    const ring = (k: number) => base + 1 + 6 * k;
    for (let i = 0; i < 6; i++) {
      // Winding chosen so the up-facing side is the front face.
      idx.set([base, ring(0) + ((i + 1) % 6), ring(0) + i], ii);
      ii += 3;
    }
    for (let k = 1; k < FAN.length; k++) {
      for (let i = 0; i < 6; i++) {
        const a = ring(k - 1) + i;
        const b = ring(k - 1) + ((i + 1) % 6);
        const cc = ring(k) + i;
        const dd = ring(k) + ((i + 1) % 6);
        idx.set([a, dd, cc, a, b, dd], ii);
        ii += 6;
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeBoundingSphere();
  return g;
}

/**
 * Mist curtain cross-section: [outward offset, height above wall base, alpha].
 * Hugs the outer face of the wall and thins out over ~2.6 tiles of "beyond".
 */
export const CURTAIN_PROFILE: ReadonlyArray<readonly [number, number, number]> = [
  [3.2, 0.0, 0.0],
  [1.6, 0.25, 0.45],
  [0.5, 0.7, 0.8],
  [0.05, 1.4, 0.85],
  [-0.15, 2.2, 0.5],
  [-0.25, 3.0, 0.0],
];

/**
 * ONE geometry: a triangle strip per boundary edge following CURTAIN_PROFILE.
 * Attribute `aA` is the alpha, `aH` the 0..1 height along the profile.
 */
export function buildCurtainGeometry(
  edges: ReadonlyArray<BoundaryEdge>,
  baseY: number,
): THREE.BufferGeometry {
  const rows = CURTAIN_PROFILE.length;
  const pos = new Float32Array(edges.length * 2 * rows * 3);
  const aA = new Float32Array(edges.length * 2 * rows);
  const aH = new Float32Array(edges.length * 2 * rows);
  const idx = new Uint32Array(edges.length * (rows - 1) * 6);
  let v = 0;
  let ii = 0;
  const top = CURTAIN_PROFILE[rows - 1]![1];
  for (const e of edges) {
    const tx = -e.nz;
    const tz = e.nx;
    const base = v;
    for (const [off, h, a] of CURTAIN_PROFILE) {
      for (const side of [-0.5, 0.5]) {
        pos.set([e.x + e.nx * off + tx * side, baseY + h, e.z + e.nz * off + tz * side], v * 3);
        aA[v] = a;
        aH[v] = h / top;
        v++;
      }
    }
    for (let r = 0; r < rows - 1; r++) {
      const a = base + r * 2;
      idx.set([a, a + 1, a + 2, a + 1, a + 3, a + 2], ii);
      ii += 6;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aA', new THREE.BufferAttribute(aA, 1));
  g.setAttribute('aH', new THREE.BufferAttribute(aH, 1));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeBoundingSphere();
  return g;
}
